import { SECOND_MS } from '@/constants';
import { debounce } from 'lodash';
import { ClientAuth } from './ClientAuth';
import { Device } from './Device';

export class Logger {
  public static applicationStartedAt: number;
  public static logs: Array<unknown> = [];

  public static loadLogs(): void {
    const logs = localStorage.getItem('bbsw:logs');
    if (logs) {
      this.logs = JSON.parse(logs);
    }
  }

  public static pushLogs(...logs: Array<unknown>): void {
    try {
      this.logs.push(...logs);
      localStorage.setItem('bbsw:logs', JSON.stringify(this.logs));
      this.debounceExportLogs();
    } catch {
      this.logs = [];
      localStorage.setItem('bbsw:logs', JSON.stringify(this.logs));
    }
  }

  public static popLogs(length: number = 500): Array<unknown> {
    try {
      const logs = this.logs.splice(0, length);
      localStorage.setItem('bbsw:logs', JSON.stringify(this.logs));
      return logs;
    } catch {
      this.logs = [];
      localStorage.setItem('bbsw:logs', JSON.stringify(this.logs));
      return [];
    }
  }

  private static get url(): string {
    if (process.env.NODE_ENV === 'development') {
      return 'https://log-receiver-test.cleve.re/logs';
    } else {
      return 'https://log-receiver.cleve.re/logs';
    }
  }

  public static debounceExportLogs = debounce(async () => {
    const logs = this.popLogs();
    if (logs.length) {
      try {
        await fetch(this.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ logger: this.appId, logs }),
        });
      } catch (e) {
        console.error(e);
      }
    }
  }, 1000);

  private static get appId(): string {
    if (Device.isTizen) {
      return 'smartstudy.co.kr_bbsworld_tizen';
    } else if (Device.isWebOS) {
      return 'smartstudy.co.kr_bbsworld_webos';
    } else {
      return 'smartstudy.co.kr_bbsworld_web';
    }
  }

  private static get appVersion(): string {
    return process.env.REACT_APP_VERSION || '1.0.0';
  }

  public static get dateString(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  }

  public static get defaultData() {
    return {
      id: Device.uuid,
      app_id: this.appId,
      app_ver: this.appVersion,
      os: Device.os,
      os_ver: Device.osVersion,
      device: Device.device,
      variant: Device.variant,
      lang: Device.language,
      country: Device.country,
      timezone: Device.timezone,
      subscribed: ClientAuth.user?.isSubscriber || undefined,
      signin: ClientAuth.user?.id || undefined,
      log_ver: 'v2',
      time: this.dateString,
    };
  }

  // ! Application 최초 설치
  public static ubid(): void {
    if (!Device.isInstalled) {
      this.pushLogs({
        ...this.defaultData,
        event: 'ubid',
      });
      localStorage.setItem('bbsw:installed', '1');
    }
  }

  // ! Application 시작 시간
  public static startApp(): void {
    this.applicationStartedAt = Date.now();
    this.pushLogs({
      ...this.defaultData,
      event: 'start_app',
    });
  }

  // ! Application 실행 기간
  public static tts(): void {
    const applicationEndedAt = Date.now();
    const applicationDuration = (applicationEndedAt - this.applicationStartedAt) / SECOND_MS;
    this.pushLogs({
      ...this.defaultData,
      event: 'tts',
      stime: applicationDuration,
    });
  }

  // ! My icon 클릭 시
  public static homeMy(): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'click_btn',
      btn: 'home_my',
    });
  }

  // ! 홈 화면 노출 및 재 진입 시
  public static exposedHome(): void {
    this.pushLogs(
      {
        ...this.defaultData,
        event: 'exposed_home',
      },
      {
        ...this.defaultData,
        event: 'click_btn',
        btn: 'home',
      },
    );
  }

  // ! 동요 아이콘 클릭 시
  public static homeNurseryRhymes(): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'click_btn',
      btn: 'nursery_rhyme',
    });
  }

  // ! 동화 아이콘 클릭 시
  public static homeFairyTales(): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'click_btn',
      btn: 'fairy_tale',
    });
  }

  // ! 영화 아이콘 클릭 시
  public static homeMovies(): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'click_btn',
      btn: 'movie',
    });
  }

  // ! 토픽 아이콘 클릭 시
  public static homeTopic(): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'click_btn',
      btn: 'home_topic',
    });
  }

  public static exposedHomeContainer(id: number, weight: number): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'exposed_container',
      container_id: id,
      weight,
    });
  }

  // ! Raw 화면 노출 시
  public static exposedRaw(
    id: number,
    sourcePage: string,
    weight: number,
    container_id?: number,
    topic_id?: number,
  ): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'exposed_raw',
      raw_id: id,
      raw_code: 'R02', // VIDEO = R02
      source_page: sourcePage,
      weight,
      container_id,
      topic_id,
    });
  }

  // ! Raw 화면 클릭 시
  public static clickRaw(
    id: number,
    sourcePage: string,
    weight: number,
    container_id?: number,
    topic_id?: number,
  ): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'click_raw',
      raw_id: id,
      raw_code: 'R02', // VIDEO = R02
      source_page: sourcePage,
      weight,
      container_id,
      topic_id,
    });
  }

  // ! Topic 화면 노출 시
  public static exposedTopic(id: number, weight: number, container_id?: number): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'exposed_topic',
      topic_id: id,
      weight,
      container_id,
    });
  }

  // ! Topic 화면 클릭 시
  public static clickTopic(id: number, weight: number, container_id?: number): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'click_topic',
      topic_id: id,
      weight,
      container_id,
    });
  }

  // ! 영상 재생 시작 시
  public static playStartRaw(id: number, sourcePage?: string, container_id: number = 0): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'play_start_raw',
      raw_id: id,
      raw_code: 'R02', // VIDEO = R02
      source_page: sourcePage,
      container_id,
    });
  }

  // ! 영상 재생 종료 시
  public static playEndRaw(id: number, duration: number, sourcePage?: string, container_id: number = 0): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'play_end_raw',
      raw_id: id,
      raw_code: 'R02', // VIDEO = R02
      play_time: duration,
      source_page: sourcePage,
      container_id,
    });
  }

  // ! 구독 팝업 노출 시
  public static exposedSubscriptionPopup(sourcePage: string, isPromotion: boolean): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'exposed_popup',
      popup: isPromotion ? 'promotion_U02' : 'subscribe_U02',
      source_page: sourcePage,
    });
  }

  // ! 구독 팝업 클릭 시
  public static clickSubscriptionPopup(sourcePage: string, isPromotion: boolean): void {
    this.pushLogs({
      ...this.defaultData,
      event: 'click_btn',
      popup: isPromotion ? 'promotion_U02_ok' : 'subscribe_U02_ok',
      source_page: sourcePage,
    });
  }
}
